import { CalendarIcon } from "@heroicons/react/24/outline";
import { useVerification } from "src/contexts/VerificationContext";
import { useEffect, useState, useCallback, useMemo } from "react";
import BlurredCard from "../BlurredCard";
import { Appointment } from "src/shared/types";
import Loading from "../Loading";
import { capitalizeStatus } from "src/utils/utils";
import { apiGet } from "src/utils/api";
import { StaticDataTable, StaticDataColumnProps, StaticDataRow } from "../table/StaticDataTable";
import { SortDirection } from "../table/useTableData";
import { useCurrentOrganization, useOrganizationContext } from "src/contexts/OrganizationContext";
import TableBadge from "../table/TableBadge";
import TableDateTime, { getDateTimeSortValue } from "../table/TableDateTime";
import PractitionerLink from "../table/PractitionerLink";

const statusColorMap: Record<string, string> = {
  booked: "bg-emerald-500",
  pending: "bg-amber-500",
  cancelled: "bg-rose-500",
  noshow: "bg-gray-500",
  "entered-in-error": "bg-rose-500",
  fulfilled: "bg-sky-500",
};

const StatusBadge = ({ status }: { status: string }) => (
  <TableBadge
    text={capitalizeStatus(status)}
    bgColorClass={statusColorMap[status] ?? "bg-gray-500"}
  />
);

const appointmentColumns: StaticDataColumnProps[] = [
  { title: "Name", sortable: true },
  { title: "Date & Time", sortable: true, defaultSort: true, defaultSortDirection: SortDirection.DESC },
  { title: "Provider", sortable: true },
  { title: "Location", sortable: true },
  { title: "Status", sortable: true },
  { title: "Reason", sortable: false },
];

const convertAppointmentsToRows = (appointments: Appointment[]): StaticDataRow[][] => {
  return appointments?.map((appointment) => [
    {
      name: "Name",
      value: appointment.name,
      element: <span className="text-gray-900">{appointment.name}</span>
    },
    {
      name: "Date & Time",
      value: getDateTimeSortValue(appointment.date, appointment.startTime),
      element: (
        <TableDateTime
          date={appointment.date}
          startTime={appointment.startTime}
          endTime={appointment.endTime}
        />
      )
    },
    {
      name: "Provider",
      value: appointment.practitioner?.name || "—",
      element: (
        <PractitionerLink
          name={appointment.practitioner?.name || "—"}
          practitionerId={appointment.practitioner?.practitioner_id}
        />
      )
    },
    {
      name: "Location",
      value: appointment.location || "—",
      element: <span className="text-gray-600 text-sm">{appointment.location || "—"}</span>
    },
    {
      name: "Status",
      value: appointment.status,
      element: <StatusBadge status={appointment.status} />
    },
    {
      name: "Reason",
      value: appointment.reasonCode || "—",
      element: <span className="text-gray-600 text-sm">{appointment.reasonCode || "—"}</span>
    },
  ]);
};

const dummyAppointments: Appointment[] = [
  {
    name: "Appointment 1",
    date: new Date().toISOString().split('T')[0],
    startTime: "10:00",
    endTime: "11:00",
    status: "booked",
    practitioner: {
      practitioner_id: "123",
      name: "Dr. John Doe",
    },
    location: "Hospital A",
    isSurgery: false,
    note: "Note 1",
    reasonCode: "Reason 1",
  },
  {
    name: "Appointment 2",
    date: new Date().toISOString().split('T')[0],
    startTime: "12:00",
    endTime: "13:00",
    status: "booked",
    practitioner: {
      practitioner_id: "456",
      name: "Dr. Jane Doe",
    },
    location: "Hospital B",
    isSurgery: true,
    note: "Note 2",
    reasonCode: "Reason 2",
  },
];

type AppointmentsCardProps = {
  yearFrom: string;
  yearTo: string;
  refreshKey?: number;
};

const AppointmentsCard = ({ yearFrom, yearTo, refreshKey }: AppointmentsCardProps) => {
  const organization = useCurrentOrganization();
  const { patientId } = useOrganizationContext();
  const [ appointments, setAppointments ] = useState<Appointment[]>([]);
  const [ loading, setLoading ] = useState(false);

  const { isVerified } = useVerification();

  const showLoading = isVerified && loading;

  const fetchAppointments = useCallback(async () => {
    if (!organization || !patientId) {
      setAppointments([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const params = { yearFrom, yearTo };
      const response = await apiGet(`/api/${organization.id}/patients/${patientId}/appointments`, params);
      const data = await response.json();

      if (data.error) {
        setAppointments([]);
        window.location.href = "/error";
      }

      setAppointments(data.appointments);
    } catch (_) {
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  }, [ organization, patientId, yearFrom, yearTo ]);

  useEffect(() => {
    if (!isVerified) {
      setAppointments(dummyAppointments);
    } else {
      fetchAppointments();
    }
  }, [ isVerified, fetchAppointments, yearFrom, yearTo, refreshKey ]);

  const tableRows = useMemo(() =>
    convertAppointmentsToRows(appointments),
    [ appointments ]
  );

  return (
    <BlurredCard>
      {showLoading ? (
        <div className="flex justify-center mt-8">
          <Loading message="Loading Appointments..." borderColor="border-brand-500" />
        </div>
      ) : (
        <div className="-mx-2">
          {appointments.length > 0 ? (
            <StaticDataTable
              tableId="appointments-table"
              columns={appointmentColumns}
              rows={tableRows}
              updateUrlParams={false}
              rowId={(item) => `${item.Name}-${item.Provider}`}
            />
          ) : (
            <div className="text-center py-12 text-gray-400">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <CalendarIcon className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm font-medium mb-4">No appointments found</p>
            </div>
          )}
        </div>
      )}
    </BlurredCard>
  );
};

export default AppointmentsCard;
